import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Button, Dropdown, Form, Icon, Input } from 'semantic-ui-react';
import CustomModal from '../../configuracion/components/customModal';
import { GetCostCenter } from '../../../graphql/costCenter';
import { AssociateCostCentersToDocument } from '../../../graphql/documents';
import { importAlertState } from '../../../redux/actions/alert';
import { setUpdateDocuments } from '../../../redux/actions/documentostributarios';
import { formatMoneyWithCurrency } from '../../../utils/formatter';
import {
  getDocumentGrossAmount,
  isCostCenterAssignmentComplete,
  COST_CENTER_EPS,
} from '../../../utils/documentGrossAmount';
import ErrorContainer from '../../../containers/ErrorContainer';
import CostCenterCatalogModal from './CostCenterCatalogModal';
import CostCenterOptionLabel from './CostCenterOptionLabel';
import './DocumentCostCentersModal.scss';
import './CostCenterOptionLabel.scss';

const parseMoneyInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? Number(digits) : 0;
};

const DocumentCostCentersModal = ({
  open,
  onClose,
  companyId,
  documentsIds = [],
  document,
  grossAmount: grossAmountProp,
  initialCostCenters = [],
  isMassive = false,
  onSuccess,
}) => {
  const { t } = useTranslation('documents');
  const dispatch = useDispatch();
  const { data: costCenterData, reexecuteQuery: reexecuteCostCenters } = GetCostCenter(companyId);
  const { executeMutation: associateCostCenters } = AssociateCostCentersToDocument();

  const grossAmount = grossAmountProp ?? (document ? getDocumentGrossAmount(document) : 0);

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  const catalogList = costCenterData?.getManyCostCenter || [];

  const costCenterOptions = useMemo(
    () =>
      catalogList.map((cc) => ({
        key: cc.id,
        text: cc.name,
        value: cc.id,
        content: (
          <CostCenterOptionLabel name={cc.name} icon={cc.icon} color={cc.color} />
        ),
      })),
    [catalogList],
  );

  useEffect(() => {
    if (!open) return;

    if (initialCostCenters?.length > 0 && !isMassive) {
      setRows(
        initialCostCenters.map((r) => ({
          id: r.id,
          label: r.label,
          amount: Number(r.amount) || 0,
          currency: r.currency || 'CLP',
        })),
      );
      return;
    }

    if (isMassive && catalogList.length > 0) {
      const first = catalogList[0];
      setRows([
        {
          id: first.id,
          label: first.name,
          amount: grossAmount,
          currency: 'CLP',
        },
      ]);
      return;
    }

    setRows(grossAmount <= COST_CENTER_EPS ? [] : [{ id: '', amount: grossAmount, currency: 'CLP' }]);
  }, [open, initialCostCenters, isMassive, grossAmount, catalogList.length]);

  const totalAssigned = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const totalError = grossAmount > COST_CENTER_EPS && totalAssigned > grossAmount + COST_CENTER_EPS;
  const ids = rows.map((r) => r.id).filter(Boolean);
  const hasDuplicateIds = new Set(ids).size !== ids.length;

  const isFormValid =
    !totalError &&
    !hasDuplicateIds &&
    (grossAmount <= COST_CENTER_EPS ||
      rows.length === 0 ||
      rows.every((r) => r.id && (Number(r.amount) || 0) > 0));

  const handleSelectCenter = (index, costCenterId) => {
    const cc = catalogList.find((c) => c.id === costCenterId);
    const next = [...rows];
    next[index] = {
      ...next[index],
      id: costCenterId,
      label: cc?.name,
    };
    setRows(next);
  };

  const handleAmountChange = (index, value) => {
    const next = [...rows];
    next[index] = { ...next[index], amount: parseMoneyInput(value) };
    setRows(next);
  };

  const handleAddRow = () => {
    const remaining = Math.max(0, grossAmount - totalAssigned);
    setRows([...rows, { id: '', amount: remaining, currency: 'CLP' }]);
  };

  const handleRemoveRow = (index) => {
    if (index === 0 && rows.length === 1) {
      setRows([]);
      return;
    }
    setRows(rows.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!companyId || !documentsIds.length || !isFormValid) return;
    setIsLoading(true);
    try {
      const payload = rows
        .filter((r) => r.id)
        .map(({ id, label, amount, currency }) => ({
          id,
          label,
          amount: Number(amount) || 0,
          currency: currency || 'CLP',
        }));

      const { data, error } = await associateCostCenters({
        companyId,
        documentsIds,
        costCenters: payload,
        isMassive,
      });
      if (error || data?.associateCostCentersToDocument?.nModified === 0) {
        throw error || new Error(t('costCenter.notSaved'));
      }
      dispatch(
        importAlertState({
          content: t('costCenter.savedOk'),
          state: 'success',
          duration: 4,
        }),
      );
      dispatch(setUpdateDocuments({ value: true }));
      onSuccess?.();
      onClose();
    } catch (err) {
      let msg = err?.message || t('costCenter.saveError');
      if (msg.startsWith('[GraphQL]')) msg = msg.replace('[GraphQL]', '').trim();
      dispatch(importAlertState({ content: msg, state: 'error', duration: 5 }));
    } finally {
      setIsLoading(false);
    }
  };

  const showCompleteBadge = isCostCenterAssignmentComplete(grossAmount, totalAssigned);

  return (
    <>
      <CustomModal
        open={open}
        onClose={onClose}
        title={t('costCenter.docModalTitle')}
        size="small"
        classNameContent="document-cost-centers-modal scfo-modal-form-body"
        confirmLabel={t('costCenter.save')}
        confirmDisabled={!isFormValid}
        isLoading={isLoading}
        onConfirm={handleSave}
        footer={t('costCenter.grossDocAmount')}
        footerTwo={grossAmount}
        content={
          <div className="document-cost-centers-modal__content">
            <p className="document-cost-centers-modal__hint">
              {t('costCenter.assignedOf', {
                assigned: formatMoneyWithCurrency(totalAssigned, 'CLP'),
                total: formatMoneyWithCurrency(grossAmount, 'CLP'),
              })}
              {showCompleteBadge && grossAmount > COST_CENTER_EPS && (
                <span className="document-cost-centers-modal__complete"> — 100%</span>
              )}
            </p>
            {grossAmount <= COST_CENTER_EPS && (
              <ErrorContainer text={t('costCenter.zeroGrossHint')} />
            )}
            {totalError && (
              <ErrorContainer text={t('costCenter.sumExceedsGross')} />
            )}
            {hasDuplicateIds && (
              <ErrorContainer text={t('costCenter.duplicateCenter')} />
            )}
            {isMassive && (
              <ErrorContainer text={t('costCenter.massiveHundredPercent')} />
            )}
            <Form>
              {rows.map((row, index) => (
                <div key={index} className="document-cost-centers-modal__row">
                  <Form.Field className="document-cost-centers-modal__field-cc">
                    <label>{t('costCenter.centerLabel')}</label>
                    <Dropdown
                      selection
                      fluid
                      search
                      placeholder={t('costCenter.selectPlaceholder')}
                      options={costCenterOptions}
                      value={row.id || undefined}
                      onChange={(_, { value }) => handleSelectCenter(index, value)}
                      disabled={isMassive && index > 0}
                    />
                  </Form.Field>
                  <Form.Field className="document-cost-centers-modal__field-amount">
                    <label>{t('costCenter.grossAmount')}</label>
                    <Input
                      value={formatMoneyWithCurrency(row.amount || 0, 'CLP')}
                      onChange={(_, { value }) => handleAmountChange(index, value)}
                      disabled={isMassive || grossAmount <= COST_CENTER_EPS}
                    />
                  </Form.Field>
                  {(rows.length > 1 || row.id) && !isMassive && (
                    <Icon
                      link
                      name="minus circle"
                      className="document-cost-centers-modal__remove"
                      onClick={() => handleRemoveRow(index)}
                    />
                  )}
                </div>
              ))}
            </Form>
            {!isMassive && grossAmount > COST_CENTER_EPS && (
              <Button basic type="button" onClick={handleAddRow} className="document-cost-centers-modal__add">
                + Agregar centro de costo
              </Button>
            )}
            <Button
              type="button"
              basic
              color="teal"
              className="document-cost-centers-modal__create-cc"
              onClick={() => setCatalogOpen(true)}
            >
              + Crear centro de costo
            </Button>
          </div>
        }
      />
      <CostCenterCatalogModal
        open={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        companyId={companyId}
        mode="create"
        onSuccess={() => reexecuteCostCenters({ requestPolicy: 'network-only' })}
      />
    </>
  );
};

export default DocumentCostCentersModal;
